import {
    formDataToObject,
    ICoordinates,
    IKeyValue,
    ISharedConfig,
    setDialog,
    setSnackbar
} from "@shared/library";
import axios, { AxiosError } from "axios";
import React, { JSX } from "react";
import config from "../../../../../config";
import { composeName } from "../../methods";
import { customDataPrefix } from "../../static";
import "./InterventionCreateForm.scss";
//#region ICONS
import { GasMeterOutlined, Settings } from "@mui/icons-material";
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import RoomRoundedIcon from '@mui/icons-material/RoomRounded';
//#endregion
//#region COMPONENTS
import GestSaveButton from "@shared/library/dist/components/gest/GestInputs/button/save/GestSaveButton";
import GestDatePicker from "@shared/library/dist/components/gest/GestInputs/pickers/date/GestDatePicker";
import GestSelect from "@shared/library/dist/components/gest/GestInputs/select/GestSelect";
import GestTextArea from "@shared/library/dist/components/gest/GestInputs/text area/GestTextArea";
//#endregion
//#region MODELS
import { IGestSelectOption } from "@shared/library/dist/components/gest/GestInputs/select/GestSelectProps";
import { IIntervention } from "../../_models/Intervention";
import { IWfmOptions } from "../InterventionManagement";
//#endregion

export interface IInterventionCreateFormProps {
    config: ISharedConfig,
    onSuccess: () => void,
    selectedPlan?: IGestSelectOption<undefined>,
    selectedCity?: IGestSelectOption<ICoordinates[][]>,
    options?: IWfmOptions,
    anomalies: { id: number | string, description?: string; }[],
    lang: any;
    customInterventionEditForm?: (props: IInterventionCreateFormProps) => JSX.Element;
};

const InterventionCreateForm = (props: IInterventionCreateFormProps) => {
    //STATES
    const createForm = React.useRef<HTMLFormElement>(null);
    const [interventionCreateLoading, setInterventionsCreateLoading] = React.useState(false);

    // METHODS
    const createIntervention = (event: React.SubmitEvent<HTMLFormElement>) => {
        event.preventDefault();

        const _ = require("lodash");
        let newObject = formDataToObject<IIntervention>(createForm.current);
        const additiveData = Object
            .keys(newObject ?? {})
            .reduce((acc, k) => {
                if (k.toLowerCase().includes(customDataPrefix) && !acc[k]) {
                    const key = k.replace(customDataPrefix, "");
                    acc[key] = newObject![k as keyof (IIntervention)];
                }

                return acc;
            }, {} as IKeyValue<any>);
        const newIntervention = {
            ...newObject,
            custom_data: Object.keys(additiveData).length ? JSON.stringify(additiveData) : undefined
        };

        setInterventionsCreateLoading(true);
        axios.post(`${config.api.internal}/WFM/Intervention/Create`, newIntervention, { withCredentials: true })
            .then(res => {
                const created = res.status === 200;

                if (created) {
                    props.onSuccess();

                    setDialog(props.config);
                    setSnackbar(props.config, { message: props.lang.successfully_created, severity: 'success' });
                }
            })
            .catch((err: AxiosError<{ detail?: string; }>) => {
                console.error(err);
                setSnackbar(props.config, { message: "Errore durante creazione dell'intervento", severity: 'error' });
            })
            .finally(() => setInterventionsCreateLoading(false));
    };

    return <form
        id="interventionEditForm"
        ref={createForm}
        onSubmit={createIntervention}
    >
        <GestSelect
            name="plan_id"
            required
            w={300}
            label={props.lang.plan}
            value={props.selectedPlan?.value}
            options={props.options?.plans.map(v => ({ value: v.id, label: composeName(v) }))}
        />

        <fieldset className="section" id="user">
            <legend><ManageAccountsRoundedIcon /> {props.lang.registry_details}</legend>

            <GestTextArea fullw name="ciu" type="number" label="CIU" required />
            <GestTextArea fullw name="supply_code" type="number" label={props.lang.supply_code} />
            <GestTextArea fullw name="supply_type" label={props.lang.supply_type} />
            <GestTextArea fullw name="email" type="email" label={props.lang.email} />
            <GestTextArea fullw name="identity_code" type="number" label={props.lang.identity_code} />
            <GestTextArea fullw name="telephone" type="tel" label={props.lang.telephone} />
            <GestTextArea fullw name="accountholder" label={props.lang.accountholder} />
        </fieldset>

        <fieldset className="section" id="geographics">
            <legend><RoomRoundedIcon /> {props.lang.geo_data}</legend>

            <GestTextArea fullw name="lat" type="number" label={props.lang.latitude} />
            <GestSelect
                name="municipality"
                fullw
                required
                w={300}
                label={props.lang.municipality}
                value={props.selectedCity?.value}
                options={props.options?.areas.map(v => ({ value: v.id, label: v.name, }))}
            />
            <GestTextArea fullw name="district" label={props.lang.district} />
            <GestSelect
                fullw
                name="old_location"
                label={props.lang.old_location}
                options={props.options?.meterLocations}
            />
            <GestTextArea fullw name="lng" type="number" label={props.lang.longitude} />
            <GestTextArea fullw label={props.lang.address} />
            <GestTextArea fullw name="alt" type="number" label={props.lang.altitude} />
            <GestSelect
                fullw
                name="new_location"
                label={props.lang.new_location}
                options={props.options?.meterLocations}
            />
        </fieldset>

        <fieldset className="section">
            <legend><GasMeterOutlined /> {props.lang.meter}</legend>

            <GestSelect
                name="old_brand"
                fullw
                label={props.lang.old_brand}
                options={props.options?.meterBrands}
            />
            <GestTextArea fullw name="old_serial" label={props.lang.old_serial} />
            <GestTextArea fullw name="old_reading" type="number" label={props.lang.old_reading} />
            <GestSelect
                name="anomaly"
                fullw
                label={props.lang.anomaly}
                options={props.anomalies.map(a => ({ value: a.id, label: a.description }))}
            />

            <GestSelect
                name="new_brand"
                fullw
                label={props.lang.new_brand}
                options={props.options?.meterBrands}
            />
            <GestTextArea fullw name="new_serial" label={props.lang.new_serial} />
            <GestTextArea fullw name="new_reading" type="number" label={props.lang.new_reading} />
            <GestTextArea fullw name="anomaly_notes" label={props.lang.anomaly_notes} />
            <GestTextArea fullw name="seal_code" label={props.lang.seal_code} />
            <GestSelect
                name="valves"
                fullw
                label={props.lang.valve}
                options={props.options?.meterValves.map(v => ({
                    value: v.code,
                    label: props.lang[v.code] ?? v.label
                })) ?? []}
            />
            <GestTextArea fullw name="new_digits" type="number" label={props.lang.digits_number} />
            <GestTextArea fullw name="diameter" type="number" minLen={0} label={props.lang.diameter_mm} />
        </fieldset>

        <fieldset className="section">
            <legend><ConstructionRoundedIcon /> {props.lang.intervention}</legend>

            <GestTextArea fullw name="odi" label="ODI" />
            <GestSelect
                name="result"
                fullw
                style={{ width: 250, maxWidth: 250 }}
                label={props.lang.intervention_result}
                options={props.options?.interventionSpecifics}
            />
            <GestDatePicker name="intervention_date" label={props.lang.intervention_date} datetime />
            <GestTextArea fullw name="annotations" label={props.lang.annotations} />

            <GestSelect
                name="type"
                fullw
                required
                label={props.lang.intervention_type}
                defaultValue={props.options?.interventionTypes.find(t => t.toLowerCase().includes("replace"))}
                options={props.options?.interventionTypes.map<IGestSelectOption<undefined>>(v => ({ value: v, label: props.lang[v], className: v }))}
            />
            <GestSelect
                name="specifics"
                fullw
                label={props.lang.intervention_specifics}
                options={props.options?.interventionSpecifics}
            />
            <GestTextArea fullw name="causal" label={props.lang.causal} />
            <GestTextArea fullw name="notes" label={props.lang.notes} />
        </fieldset>

        {props.config.session?.userData?.custom_data &&
            <fieldset className="section" id="customData">
                <legend><Settings /> {props.lang?.custom_data}</legend>

                {Object
                    .keys(JSON.parse(props.config.session.userData.custom_data))
                    .map(k => <GestTextArea key={`${customDataPrefix}${k}`} fullw name={`${customDataPrefix}${k}`} label={props.lang?.[k] ?? k} />)}
            </fieldset>
        }

        <GestSaveButton alignment='right' submit label={props.lang.create_new} loading={interventionCreateLoading} />
    </form >;
};
export default React.memo(InterventionCreateForm);
